import { useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DataGrid, itIT, GridToolbar, useGridApiRef } from '@mui/x-data-grid';		//https://mui.com/x/react-data-grid/ [MIT version]
import { Box, Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, FormControl, InputLabel, Select, MenuItem } from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ChatIcon from '@mui/icons-material/Chat';
import DownloadIcon from '@mui/icons-material/Download';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Functions from "../../Services/Functions";
import loadingGif from '../../Assets/loading.gif';

import Api from '../../Services/Api';
import {PAGE_SIZE, pageSizeOptions, colorDataBackground} from '../../Config/constants.js';
import ActionButton from '../ActionButton.js';

function CustomerList(props) {
	const { hook, lastUpdate } = props;
	const apiRef = useGridApiRef();	//https://mui.com/x/api/data-grid/

    const [loading, setLoading] = useState(false)
    const [installationsDialogRow, setInstallationsDialogRow] = useState(null)
    const [colorFilter, setColorFilter] = useState("")

    const ColorClasses = () => {
        const generatedClasses = colorDataBackground.map((color) => {
            const className = color.name.toLowerCase(); // Convert name to lowercase for class name
            const backgroundColor = color.hex;

            return `.${className} {
                background-color: ${backgroundColor};
            }`;
        });

        return (
            <div className="color-classes-container">
                <style>{generatedClasses.join('\n')}</style>
            </div>
        );
    };


	const columns = [
		{
			field: 'surname',
			headerName: 'Cognome',
			flex: 1,
		},
		{
			field: 'name',
			headerName: 'Nome',
			flex: 1,
		},
		{
			field: 'fiscalVatCode',
			headerName: 'C.F./P.Iva',
			flex: 2,
		},
		{
			field: 'address',
			headerName: 'Indirizzo',
			flex: 2,
		},
		{
			field: 'city',
			headerName: 'Città',
			flex: 2,
		},
		{
			field: 'phone_number_1',
			headerName: 'Telefono',
			flex: 1,
		},
		{
			field: 'verified',
			headerName: 'Verificato',
			type: 'boolean',
			valueGetter: (params) => {
				return params.row.verified ? true : false
			},
		},
		{
			field: 'color',
			headerName: 'Colore',
			flex: 1,
		},
		{
			field: 'installations_list',
			headerName: 'Codici Impianto / Matricole',
			flex: 2,
			sortable: false,
			valueGetter: (params) => {
				if (!Array.isArray(params.row.installations_list)) return '';
				return params.row.installations_list
					.map(i => `${i.unique_code ?? ''} ${i.serial ?? ''}`)
					.join(' ');
			},
		},
		{
			field: 'id',
			headerName: 'Azioni',
			flex: 1,
			renderCell: (params) => {
				function sendSms(e) {
					e.stopPropagation(); // don't select this row after clicking
					hook("sendSms", params.value);
				};
				function onEdit(e) {
					e.stopPropagation(); // don't select this row after clicking
					hook("edit", params.value);
				};
				function onDelete(e) {
					e.stopPropagation(); // don't select this row after clicking
					if (window.confirm("Confermare l'eliminazione DEFINITIVA del Cliente?")) {
						if (window.confirm("Eliminare il Cliente?")) {
							hook("delete", params.value);
						}
					}
				};
				function onExport(e) {
					e.stopPropagation(); // don't select this row after clicking
					downloadCustomerPDF(params.row.id);
				};
				function onToggleInstallations(e) {
					e.stopPropagation();
					setInstallationsDialogRow(params.row);
				};
				return (
                    !loading ? (
                        <>
                            <ActionButton title={"Codici Impianto"} onClick={onToggleInstallations} disabled={!params.row.installations_list?.length}>
                                <LocalFireDepartmentIcon />
                            </ActionButton>
                            <ActionButton title={"Invia SMS"} onClick={sendSms}>
                                <ChatIcon />
                            </ActionButton>
                            <ActionButton title={"Modifica Cliente"} onClick={onEdit}>
                                <EditIcon />
                            </ActionButton>
                            <ActionButton title={"Elimina Cliente"} onClick={onDelete}>
                                <DeleteIcon />
                            </ActionButton>
                            <ActionButton title={"Esporta Scheda Cliente"} onClick={onExport}>
                                <DownloadIcon />
                            </ActionButton>
                        </>
                    ) : (
                        <img className="pl-3" src={loadingGif} alt="Loading..."/>
                    )
                )
			}
		},
	]

	function downloadCustomerPDF(customerId) {
		try {
            setLoading(true);
			Api.get({endpoint: `customer/download/${customerId}`, responseType : 'blob'})
				.then(response => {
                    Functions.openBlob(response.data);
                    setLoading(false);
				})
				.catch(error => {
										setDisplayAlert(-1);
                    setLoading(false);
				});
		} catch (error) {
						setDisplayAlert(-1);
		}
	};

	function newElement() {
		hook("create");
	}

	const [rows, setRows] = useState([]);
	const [displayAlert, setDisplayAlert] = useState(0);

	useEffect(() => {
		try {
			Api.get({endpoint: `customer`, contentType : 'application/json'})
			.then(response => {
				setRows(response.data.customers);
			})
			.catch(error => {
								setDisplayAlert(-1);
			});
		} catch (error) {
						setDisplayAlert(-1);
		}
	}, [lastUpdate]);	// eslint-disable-line react-hooks/exhaustive-deps

	const theme = createTheme({
			palette: {
				primary: { main: '#11cb5f' },
			},
		},
		itIT,
	);

	return (
		<>
            <ColorClasses />
			<h5>Clienti</h5>
			<Box sx={{ width: '100%', minWidth: '800px' }}>
				<ThemeProvider theme={theme}>
					<Box sx={{ mb: 1 }}>
						<FormControl size="small" sx={{ minWidth: 180 }}>
							<InputLabel id="color-filter-label">Filtra per colore</InputLabel>
							<Select
								labelId="color-filter-label"
								value={colorFilter}
								label="Filtra per colore"
								onChange={(e) => {
									const val = e.target.value;
									setColorFilter(val);
									apiRef.current.setFilterModel({
										items: val ? [{ field: 'color', operator: 'equals', value: val }] : []
									});
								}}
							>
								<MenuItem value=""><em>Tutti</em></MenuItem>
								{colorDataBackground.map((c) => (
									<MenuItem key={c.name} value={c.name}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: c.hex, border: '1px solid #ccc' }} />
											{c.label}
										</Box>
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
					<DataGrid
						//onRowDoubleClick={hook}
						sx={{ minHeight: '300px' }}
						rows={rows}
						columns={columns}
                        getRowClassName={(params) => `${params.row.color}`}
						initialState={{
							pagination: {
								paginationModel: {
									pageSize: PAGE_SIZE,
								},
							},
							columns: {
								columnVisibilityModel: { installations_list: false, color: false },
							},
						}}
						apiRef={apiRef}
						slots={{
							toolbar: GridToolbar
						}}
						slotProps={{
							columnsPanel: {
								disableHideAllButton: true,
							},
							toolbar: {
								showQuickFilter: true
							}
						}}
						pageSizeOptions={pageSizeOptions}
					/>
					<div className="mt-2">
						<Button
							variant="contained"
							onClick={newElement}
						>
							Nuovo Cliente
						</Button>
					</div>
				</ThemeProvider>
				{displayAlert === -1 && (
					<Alert severity="error">Si è verificato un errore imprevisto. Riprovare più tardi</Alert>
				)}
			</Box>

			<Dialog
				open={installationsDialogRow !== null}
				onClose={() => setInstallationsDialogRow(null)}
				aria-labelledby="installations-dialog-title"
			>
				<DialogTitle id="installations-dialog-title">
					Impianti — {installationsDialogRow?.surname} {installationsDialogRow?.name}
				</DialogTitle>
				<DialogContent>
					{installationsDialogRow?.installations_list?.length === 0 && (
						<Box>Nessun impianto associato</Box>
					)}
					{installationsDialogRow?.installations_list?.map((inst, i) => (
						<Box key={inst.id ?? i} sx={{ py: 1, borderBottom: i < installationsDialogRow.installations_list.length - 1 ? '1px solid #e0e0e0' : 'none', display: 'flex', alignItems: 'center', gap: 2 }}>
							<Box sx={{ flex: 1 }}>
								<Box><strong>Codice impianto:</strong> {inst.unique_code}</Box>
								<Box><strong>Matricola:</strong> {inst.serial}</Box>
							</Box>
							<IconButton size="small" href={`/cms/installations/edit/${inst.id}/${installationsDialogRow.id}`} title="Apri impianto">
								<OpenInNewIcon fontSize="small" />
							</IconButton>
						</Box>
					))}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setInstallationsDialogRow(null)}>Chiudi</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

export default CustomerList;
